import type { Theme } from "@mui/material/styles/createTheme";
import { styled, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useMobileNav } from "hooks/use-mobile-nav";
import { useNavReturnFocus } from "hooks/use-nav-return-focus";
import TopNav from "./components/TopNav";
import MobileNav from "./components/MobileNav";
import { useMakeUserAllowedSections } from "layouts/Config";
import SideNav from "./components/SideNav";
import { useAppSelector } from "hooks/use-app-select";
import { useAppDispatch } from "redux/hooks";
import {
  selectSideNavCollpased,
  updateSideNavCollpased,
} from "redux/preferencesSlice";
import viewpaths from "global/constants/ViewPathConstants";
import AnnouncementBanner from "global/components/AnnoucementBanner/AnnouncementBanner";
import { PageHeaderProvider } from "global/components/PageHeader/PageHeaderContext";
import { isOfferActive } from "helpers/methods";

const isSettingsRoute = (pathname: string) =>
  pathname.startsWith(viewpaths.settingsViewPath) ||
  pathname.startsWith(viewpaths.ticketsViewPath);

export const SIDE_NAV_WIDTH = 280;
export const SIDE_NAV_COLLAPSED_WIDTH = 70;

// Defined at module scope (NOT inside the component) so their component
// identity stays stable across renders. Declaring styled() inside the render
// created a new component type on every render — including sidebar toggles —
// which remounted the whole page subtree and re-fired all mount-time API calls.
const VerticalLayoutRoot = styled("div", {
  shouldForwardProp: (prop) => prop !== "isCollapsed" && prop !== "offerActive",
})<{ isCollapsed: boolean; offerActive: boolean }>(
  ({ theme, isCollapsed, offerActive }) => ({
    display: "flex",
    flex: "1 1 auto",
    maxWidth: "100%",
    transition: "padding-left 0.3s ease-in-out",
    paddingTop: 0,
    [theme.breakpoints.down("lg")]: {
      paddingTop: offerActive ? "25px" : 0,
    },
    [theme.breakpoints.up("lg")]: {
      paddingLeft: isCollapsed ? SIDE_NAV_COLLAPSED_WIDTH : SIDE_NAV_WIDTH,
    },
  }),
);

const VerticalLayoutContainer = styled("div")({
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  width: "100%",
});

interface CustomProps {
  children: any;
}

const DrawerLayout = (props: CustomProps) => {
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("lg"));
  const mobileNav = useMobileNav();
  const sections = useMakeUserAllowedSections();
  const sideNavCollpased = useAppSelector(selectSideNavCollpased);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const theme = useTheme();

  useNavReturnFocus();

  const preSettingsCollapseRef = useRef<boolean | null>(null);
  const currentCollapseRef = useRef<boolean>(sideNavCollpased);
  currentCollapseRef.current = sideNavCollpased;

  useEffect(() => {
    const onSettings = isSettingsRoute(location.pathname);
    if (onSettings) {
      if (preSettingsCollapseRef.current === null) {
        preSettingsCollapseRef.current = currentCollapseRef.current;
        if (!currentCollapseRef.current) {
          dispatch(updateSideNavCollpased(true));
        }
      }
    } else if (preSettingsCollapseRef.current !== null) {
      // Always expand the sidebar when leaving the settings/tickets layout,
      // regardless of its collapse state before entering settings.
      dispatch(updateSideNavCollpased(false));
      preSettingsCollapseRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    // The app bar and the routed screen sit side by side here, so the page
    // header registry has to wrap both for a screen to reach `TopNav`.
    <PageHeaderProvider>
      {isOfferActive() && (
        <AnnouncementBanner
          announcement="Save 20% on the Annual Plan! Apply coupon INKFREE20 before the offer ends. 🎉"
          backgroundColor={theme.palette.primary.main}
          show={true}
        />
      )}
      <div style={{ position: "sticky", top: 0, zIndex: 1100 }}>
        <TopNav onMobileNavOpen={mobileNav.handleOpen} />
      </div>
      {lgUp && <SideNav sections={sections} />}
      {!lgUp && (
        <MobileNav
          onClose={mobileNav.handleClose}
          open={mobileNav.open}
          sections={sections}
        />
      )}
      <VerticalLayoutRoot
        isCollapsed={sideNavCollpased}
        offerActive={isOfferActive()}
      >
        <VerticalLayoutContainer>{props.children}</VerticalLayoutContainer>
      </VerticalLayoutRoot>
    </PageHeaderProvider>
  );
};

export default DrawerLayout;
